import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../../common/reducers/initContext";
import { initMapFactory, DefaultFailureAction, DefaultSuccessAction, initMultiMapFactory, dataHookWithCredentialsFactory, queryMapFactory } from "../../common/reducers/utilities";
import { ensureArray } from "../../common/utils/arrayUtilities";
import { useMemo } from "react";
import { ensureInteger } from "../../common/utils/generalUtils";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";
import { memberSearch } from "./memberUtils";

const prepareArray = data => ensureArray(data).map(member => ({
    ...member,
    name: member.preferredname ?? member.firstname,
    fullname: (member.preferredname ?? member.firstname) + ' ' + member.lastname,
    // TODO remove once customtables is deprecated needed for ACES computed fields like classification
    customfields: { ...member.customfields, ...member.customtables },
}))

export const memberSuperSlice = createSlice({
    name: 'adminMembers',
    initialState: {
        data: {},
        pictures: {},
        inactiveMembers: {},
    },
    reducers: {
        setActive: (state, action) => {
            // we can do the following because we always fetch all the members
            state.data = {};
            prepareArray(action.payload).forEach(m => state.data[m.id] = m);
        },
        setInactive : (state, action) => {
            state.inactiveMembers = {};
            prepareArray(action.payload).forEach(m => state.inactiveMembers[m.id] = m);
        },
        set: (state, action) => {
            const toProcess = new Set(action.payload.ids.map(id => ensureInteger(id)));
            prepareArray(action.payload.data).forEach(m => {
                const id = ensureInteger(m.id)
                if (m.inactive) {
                    delete state.data[id];
                    state.inactiveMembers[id] = m;
                } else {
                    state.data[id] = m;
                    delete state.inactiveMembers[id];
                }
                toProcess.delete(m.id);
            });
            toProcess.forEach(id => {
                delete state.data[id];
                delete state.inactiveMembers[id];
            });
        },
        addOrUpdate: (state, action) => {
            prepareArray(action.payload).forEach(m => {
                const id = ensureInteger(m.id);
                if (m.inactive) {
                    delete state.data[id];
                    state.inactiveMembers[id] = m;
                } else {
                    state.data[id] = m;
                    delete state.inactiveMembers[id];
                }
            })
        },
        addOrUpdatePicture: (state, action) => {
            ensureArray(action.payload).forEach(p => state.pictures[ensureInteger(p.id)] = p);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(m => {
                const id = ensureInteger(m.id);
                if (state.data[id] && m.inactive) {
                    delete state.data[id];
                    state.inactiveMembers[id] = m
                }
            })
        },
        removePicture: (state, action) => {
            ensureArray(action.payload).forEach(p => delete state.pictures[ensureInteger(p.id)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => memberSuperSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'staff_one', action, data => ({ name: `${data.firstname} ${data.lastname}`,
    id: data.id 
}));
const visualizeFailure = verb => DefaultFailureAction(verb, 'staff_one');

const fetchAllActiveMembers = network => network.get('/api/v2/adminMembers/active', memberSuperSlice.actions.setActive, visualizeFailure('get'));
const fetchAllInactiveMembers = network => network.get('/api/v2/adminMembers/inactive' , memberSuperSlice.actions.setInactive, visualizeFailure('get')); 
const fetchMember = aggregatingFetch(
    id => network => network.post('/api/v2/adminMembers', [id], data => memberSuperSlice.actions.set({ data, ids: [id] }), visualizeFailure('get')),
    ids => network => network.post('/api/v2/adminMembers', Array.from(ids), data => memberSuperSlice.actions.set({ data, ids }), visualizeFailure('get')),
    { primitiveKey: true }
);
const fetchPicture = id => network => network.get(`/api/v2/memberPicture/${encodeURIComponent(id)}`, memberSuperSlice.actions.addOrUpdatePicture, visualizeFailure('get'));
export const createMember = (network, member) => network.post('/api/v1/member', member, visualizeSuccess('create', data => memberSuperSlice.actions.addOrUpdate(data)), visualizeFailure('create'));
export const updateMember = (network, delta) => network.put('/api/v1/member', delta, data => memberSuperSlice.actions.addOrUpdate(data), visualizeFailure('update'));
export const deleteMember = (network, id) => network.delete('/api/v1/member/' + window.encodeURIComponent(id), memberSuperSlice.actions.remove, visualizeFailure('delete'));
// #endregion

// #region selectors
const selectMemberData = state => state.adminMembers.data;
const selectAllMemberData = state => state.adminMembers;
const selectMembersById = ids => createSelector([selectAllMemberData], data => ids.reduce((res, id) => { const m = data.data[id] ?? data.inactiveMembers[id]; if (m) { res.push(m) } return res; }, []));
const selectMemberById = id => createSelector([selectAllMemberData], data => data.data[id] ?? data.inactiveMembers[id])
const selectAllActiveMembers = createSelector([selectMemberData], (members) => Object.values(members));
const selectInactiveMemberData = state => state.adminMembers.inactiveMembers;
const selectAllInactiveMembers = createSelector([selectInactiveMemberData], (members) => Object.values(members));
const selectSearchActiveMembers = search => createSelector([selectMemberData], members => memberSearch(members, search));
const selectSearchInactiveMembers = search => createSelector([selectInactiveMemberData], members => memberSearch(members, search));
const selectAllActiveMembersMap = createSelector([selectMemberData], data => data);
const selectMemberMapById = ids => createSelector([selectMemberData], data => {
    return ids.reduce((acc, id) => {
        const memberData = data[id]
        if (memberData) {
            acc[id] = memberData
        } 
        return acc;
    }, {});
});

const selectPictureData = state => state.adminMembers.pictures;
const selectPicture = id => createSelector([selectPictureData], pictures => pictures[id]);

// #endregion

const listen = {
    memberAdminCreated: (member, { dispatch, websocket }) => {
        markFetched(websocket, 'member', member.id);
        dispatch(memberSuperSlice.actions.addOrUpdate(member))
    },
    memberAdminUpdated: (member, { dispatch }) => dispatch(memberSuperSlice.actions.addOrUpdate(member)),
    memberAdminRemoved: (member, { dispatch }) => dispatch(memberSuperSlice.actions.remove(member))
}

const pictureListen = {
    memberPictureUpdated: (data, { dispatch }) => dispatch(memberSuperSlice.actions.addOrUpdatePicture(data))
}
// #endregion

// #region permissions
const fullRoles = ['administrator', 'member manager'];
const readRoles = ['auditor', 'member auditor', 'training manager', 'training auditor'];
const checkPermissions = (credentials, member, requiredRoles, condition) => { 
    const access = credentials.hasRole(requiredRoles, {office: member?.office, client: member?.default_client, location: member?.default_location});
    return (access && condition !== false) ?? false 
}
export const canSuperCreateMember = (credentials, member) => checkPermissions(credentials, member, fullRoles);
export const canSuperUpdateMember = (credentials, member) => checkPermissions(credentials, member, fullRoles, !member.inactive);
export const canSuperRemoveMember = canSuperCreateMember;
export const canSuperReactivateMember = (credential, member) => checkPermissions(credential, member, fullRoles, member.inactive);
export const canSuperViewMembers = (credentials) => checkPermissions(credentials, null, fullRoles.concat(readRoles));
export const canSuperViewMember = (credentials, member) => checkPermissions(credentials, member, fullRoles.concat(readRoles));
// #endregion

const optionsMultiple = { condition: canSuperViewMembers }
const optionsSingle = { condition: canSuperViewMembers, defaultValue: null}

const initMemberByID = initMapFactory(fetchMember, listen);
registerEntityBasedFetch('member', initMemberByID);

const initActiveMembers = queryMapFactory(fetchAllActiveMembers, 'id', initMemberByID, listen, { allowEmpty: true, name: 'activeSuperMembers', })();
const initInactiveMembers = queryMapFactory(fetchAllInactiveMembers, 'id', initMemberByID, listen, { allowEmpty: true, name: 'inactiveSuperMewmbers'})();
export const [useSuperAllActiveMembers] = dataHookWithCredentialsFactory(selectAllActiveMembers, initActiveMembers, optionsMultiple);
export const [useSuperAllInactiveMembers] = dataHookWithCredentialsFactory(selectAllInactiveMembers, initInactiveMembers, optionsMultiple);
export const [useSuperActiveMemberSearch] = dataHookWithCredentialsFactory(selectSearchActiveMembers, search => initActiveMembers, optionsMultiple);
export const [useSuperInactiveMemberSearch] = dataHookWithCredentialsFactory(selectSearchInactiveMembers, search => initInactiveMembers, optionsMultiple);
export const [useSuperMembers] = dataHookWithCredentialsFactory(selectMembersById, initMultiMapFactory(initMemberByID, listen, optionsMultiple));
export const [useSuperMember, realizeSuperMember] = dataHookWithCredentialsFactory(selectMemberById, initMemberByID, optionsSingle);
export const [useSuperMembersMap] = dataHookWithCredentialsFactory(selectMemberMapById, initMultiMapFactory(initMemberByID, listen, optionsMultiple));
export const [useSuperAllActiveMemberMap] = dataHookWithCredentialsFactory(selectAllActiveMembersMap, initActiveMembers, optionsMultiple);
export const resetMemberPassword = (network, id) => network.post('/api/v2/resetMemberUserPassword', [id], null, visualizeFailure('update'));

const pictureInitByID = initMapFactory(fetchPicture, pictureListen, { name: 'pictureByID' });
const pictureOptions = {
    cleanup: { root: memberSuperSlice.name, all: state => Object.values(selectPictureData(state)), remove: data => memberSuperSlice.actions.removePicture(data) },
    condition: canSuperViewMembers,
    defaultValue: null
}
export const [useMemberPicture, realizeMemberPicture] = dataHookWithCredentialsFactory(selectPicture, pictureInitByID, pictureOptions);

export const useSuperAllMembers = () => {
    const activeMembers = useSuperAllActiveMembers();
    const inactiveMembers = useSuperAllInactiveMembers();

    return useMemo(() => [...activeMembers, ...inactiveMembers], [activeMembers, inactiveMembers]);
}

const reducer = { name: memberSuperSlice.name, reducer: memberSuperSlice.reducer }
export default reducer;